/**
 * EL ENLACE DE CONFIRMACION PARA CLIENTES QUE YA ESTAN (CLI-ACT-T1, mision CLIENTES-ACTUALIZACION-DATOS-V1-001).
 *
 * El mismo formulario #/onboard/{token}, pero cuando la API dice modo 'confirmacion' (nexo-api 717, Ares #40215): cada
 * pregunta que la ficha ya tiene muestra lo que Nexo tiene y el cliente dice -sigue igual- o -corregir-.
 *
 * El contrato (ep-onboarding-token y ep-onboarding-responses):
 *   - GET token trae modo ('confirmacion' | 'alta') y actual {campo: valor}; cedula, telefonos, correo y NIT vienen
 *     enmascarados (***1234, d***@dominio). Lo decide la API; el panel no manda el modo.
 *   - Al cerrar, la API toma de la ficha toda clave que el cliente NO marco en payload.corregidos. Por eso lo confirmado
 *     no se valida ni se reenvia, y un dato personal corregido se escribe COMPLETO (nunca se reenvia el enmascarado).
 *   - corregidos se acumula entre envios.
 *
 * Sin DOM: lo prueba scripts/check-onboard-confirmacion.mjs.
 *
 * @module application/onboarding/confirmacionDelRegistro
 */

/** Las claves del cuestionario que la API puede traer en actual (contrato de ep-onboarding-token). */
export const CAMPOS_CONFIRMABLES = ['legalName', 'taxId', 'businessType', 'addressLine1', 'department', 'city',
  'ownerName', 'ownerId', 'ownerPhone', 'ownerEmail', 'businessPhone', 'horario'];

/** Las que viajan enmascaradas: corregirlas pide escribirlas completas. */
export const ENMASCARADOS = ['taxId', 'ownerId', 'ownerPhone', 'ownerEmail', 'businessPhone'];

/** Corregir una de estas obliga a corregir tambien las de la lista (el municipio depende del departamento). */
const ARRASTRA = { department: ['city'] };

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());

/** Un valor que viene tapado por la API (lleva ***). */
export const estaEnmascarado = (v) => texto(v).includes('***');

/**
 * Lo que dice el GET token, en camel o Pascal. Sin modo 'confirmacion' es alta, aunque traiga actual (fail-open: la API
 * vieja no manda modo y el formulario sigue como siempre). -> {modo, actual}
 */
export function leerModo(data) {
  const d = data && typeof data === 'object' ? data : {};
  const modo = texto(d.modo ?? d.Modo).toLowerCase() === 'confirmacion' ? 'confirmacion' : 'alta';
  const crudo = d.actual ?? d.Actual;
  const actual = {};
  if (modo === 'confirmacion' && crudo && typeof crudo === 'object') {
    for (const k of CAMPOS_CONFIRMABLES) if (texto(crudo[k])) actual[k] = texto(crudo[k]);
  }
  return { modo, actual };
}

/**
 * Los valores con que arranca el formulario en confirmacion: lo que el cliente ya escribio (borrador o sesion) manda;
 * lo demas sale de actual, salvo lo enmascarado, que nunca entra a los valores (no se valida ni se reenvia).
 */
export function valoresDeConfirmacion(actual, capturado) {
  const v = {};
  for (const [k, val] of Object.entries(actual || {})) if (!ENMASCARADOS.includes(k) && !estaEnmascarado(val)) v[k] = val;
  return { ...v, ...(capturado || {}) };
}

/** Las claves que quedan como estan: las que Nexo tiene y el cliente no corrigio. Solo en confirmacion. */
export function confirmados(modo, actual, corregidos) {
  if (modo !== 'confirmacion') return new Set();
  const c = corregidos instanceof Set ? corregidos : new Set(corregidos || []);
  return new Set(Object.keys(actual || {}).filter((k) => !c.has(k)));
}

/** Corregir una clave, con lo que arrastra (department -> city). Devuelve el nuevo Set. */
export function corregir(corregidos, clave) {
  const c = new Set(corregidos || []);
  c.add(clave);
  for (const k of ARRASTRA[clave] || []) c.add(k);
  return c;
}

/** Volver a dejar una clave como estaba (y lo que arrastra). Devuelve el nuevo Set. */
export function dejarComoEstaba(corregidos, clave) {
  const c = new Set(corregidos || []);
  c.delete(clave);
  for (const k of ARRASTRA[clave] || []) c.delete(k);
  return c;
}

/** Lo que vale una clave al volver a dejarla como estaba: el de la ficha, o nada si viene enmascarado. */
export function valorAlDejar(actual, clave) {
  const v = (actual || {})[clave];
  return v && !ENMASCARADOS.includes(clave) && !estaEnmascarado(v) ? v : undefined;
}

/** Los errores de un paso sin los de lo confirmado: lo que no se toca no se valida (la API lo toma de la ficha). */
export function sinErroresDeConfirmados(errores, conf) {
  const out = {};
  for (const [k, v] of Object.entries(errores || {})) if (!conf.has(k)) out[k] = v;
  return out;
}

/**
 * El cuerpo de POST responses en confirmacion: sin lo confirmado (la API lo toma de la ficha y un enmascarado no puede
 * viajar) y con corregidos. En alta, el cuerpo de siempre.
 */
export function cuerpoConCorregidos(modo, cuerpo, conf, corregidos) {
  if (modo !== 'confirmacion') return cuerpo;
  const out = {};
  for (const [k, v] of Object.entries(cuerpo || {})) {
    if (conf.has(k)) continue;
    if (typeof v === 'string' && estaEnmascarado(v)) continue;
    out[k] = v;
  }
  const c = [...(corregidos || [])].filter((k) => CAMPOS_CONFIRMABLES.includes(k)).sort();
  if (c.length) out.corregidos = c;
  return out;
}

/** Para el resumen: lo confirmado se muestra como lo tiene Nexo (enmascarado incluido). */
export function valoresParaMostrar(valores, actual, conf) {
  const v = { ...(valores || {}) };
  for (const k of conf) if ((actual || {})[k]) v[k] = actual[k];
  return v;
}

/** Los textos de la pantalla en confirmacion. */
export const TEXTOS_CONFIRMACION = {
  titulo: 'Confirma los datos de tu negocio',
  intro: 'Esto es lo que Nexo tiene de tu negocio. Si algo cambió, toca Corregir y escríbelo como debe quedar.',
  tiene: 'Nexo tiene',
  corregir: 'Corregir',
  dejar: 'Dejar como estaba',
  completo: 'Escríbelo completo: por seguridad no te mostramos el dato entero.',
  sinCambios: '(sin cambios)'
};
